// src/pages/channels/ConfigureWhatsAppQR.js
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, RefreshCw, CheckCircle, 
  Loader2, Smartphone, Wifi, WifiOff, QrCode,
  MessageSquare, Phone
} from 'lucide-react';
import { channelService } from '../../services/PlatformService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const ConfigureWhatsAppQR = () => {
  const { channelId } = useParams();
  const navigate = useNavigate();
  
  const [channel, setChannel] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [qrLoading, setQrLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const [connectedNumber, setConnectedNumber] = useState(null);

  useEffect(() => {
    loadChannel();
  }, [channelId]);

  useEffect(() => {
    if (channel && currentStep === 2) {
      checkConnectionStatus();
      
      // Poll status every 5 seconds if not connected
      const interval = setInterval(() => {
        if (!connectionStatus?.connected) {
          checkConnectionStatus();
        }
      }, 5000);
      
      return () => clearInterval(interval);
    }
  }, [channel, currentStep, connectionStatus?.connected]);

  const loadChannel = async () => {
    try {
      setLoading(true);
      const response = await channelService.getChannel(channelId);
      const channelData = response.data;
      
      if (channelData.type !== 'whatsapp_qr') {
        navigate('/channels');
        return;
      }
      
      setChannel(channelData);
      
      // Verificar se já está configurado
      if (channelData.instance_id) {
        setCurrentStep(2); // Pular para step de conexão
      }
    } catch (error) {
      console.error('Error loading channel:', error);
      alert('Erro ao carregar canal');
      navigate('/channels');
    } finally {
      setLoading(false);
    }
  };

  const checkConnectionStatus = async () => {
    try {
      setStatusLoading(true);
      
      const response = await fetch(`${API_BASE_URL}/channels/${channelId}/whatsapp-qr/status`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      
      if (response.ok) {
        const statusData = await response.json();
        setConnectionStatus(statusData);
        
        if (statusData.connected) {
          setCurrentStep(3); // Connected, go to final step
          setConnectedNumber(statusData.number || 'Número conectado');
        } else {
          // Try to get QR code if not connected
          await getQRCode();
        }
      }
    } catch (error) {
      console.error('Error checking status:', error);
    } finally {
      setStatusLoading(false);
    }
  };

  const getQRCode = async () => {
    try {
      setQrLoading(true);
      
      const response = await fetch(`${API_BASE_URL}/channels/${channelId}/whatsapp-qr/qr-code`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      
      if (response.ok) {
        const qrData = await response.json();
        if (qrData.qr) {
          setQrCode(qrData.qr);
        }
      }
    } catch (error) {
      console.error('Error getting QR code:', error);
    } finally {
      setQrLoading(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Tem certeza que deseja desconectar o WhatsApp?')) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/channels/${channelId}/whatsapp-qr/disconnect`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      
      if (response.ok) {
        setCurrentStep(2);
        setConnectionStatus({ connected: false });
        setConnectedNumber(null);
        setQrCode(null);
        await checkConnectionStatus();
      } else {
        alert('Erro ao desconectar');
      }
    } catch (error) {
      console.error('Error disconnecting:', error);
      alert('Erro ao desconectar');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-green-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-blue-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate('/channels')}
            className="p-2 hover:bg-gray-100 rounded-xl transition-all"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">
            Configurar {channel?.name}
          </h1>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between">
          <div className={`flex items-center gap-2 ${currentStep >= 1 ? 'text-green-600' : 'text-gray-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
              currentStep >= 1 ? 'bg-green-600 text-white' : 'bg-gray-200'
            }`}>
              1
            </div>
            <span className="font-medium">Instância</span>
          </div>
          
          <div className={`flex-1 h-1 mx-4 ${currentStep >= 2 ? 'bg-green-600' : 'bg-gray-200'}`} />
          
          <div className={`flex items-center gap-2 ${currentStep >= 2 ? 'text-green-600' : 'text-gray-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
              currentStep >= 2 ? 'bg-green-600 text-white' : 'bg-gray-200'
            }`}>
              2
            </div>
            <span className="font-medium">Conexão</span>
          </div>
          
          <div className={`flex-1 h-1 mx-4 ${currentStep >= 3 ? 'bg-green-600' : 'bg-gray-200'}`} />
          
          <div className={`flex items-center gap-2 ${currentStep >= 3 ? 'text-green-600' : 'text-gray-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
              currentStep >= 3 ? 'bg-green-600 text-white' : 'bg-gray-200'
            }`}>
              3
            </div>
            <span className="font-medium">Pronto!</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {currentStep === 1 && (
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-xl font-bold text-gray-900 mb-6">
              Etapa 1: Instância Configurada
            </h2>
            
            <div className="bg-green-50 border border-green-200 rounded-xl p-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-green-900">
                    Instância WhatsApp Pronta
                  </h3>
                  <p className="text-green-700">
                    Sua instância foi configurada e está pronta para conectar ao WhatsApp.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setCurrentStep(2)}
                className="px-6 py-3 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 transition-all"
              >
                Continuar para Conexão
              </button>
            </div>
          </div>
        )}

        {currentStep === 2 && (
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">
                Etapa 2: Conectar WhatsApp
              </h2>
              <button
                onClick={checkConnectionStatus}
                disabled={statusLoading}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition-all"
                title="Atualizar status"
              >
                <RefreshCw className={`w-5 h-5 ${statusLoading ? 'animate-spin' : ''}`} />
              </button>
            </div>

            {connectionStatus?.connected ? (
              <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                    <Wifi className="w-8 h-8 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-green-900">
                      WhatsApp Conectado
                    </h3>
                    <p className="text-green-700">
                      Número: {connectedNumber}
                    </p>
                  </div>
                  <button
                    onClick={handleDisconnect}
                    className="px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-all"
                  >
                    Desconectar
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <Smartphone className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-800">
                      <p className="font-medium mb-2">Como conectar:</p>
                      <ol className="list-decimal list-inside space-y-1">
                        <li>Abra o WhatsApp no seu celular</li>
                        <li>Vá em Configurações → Aparelhos conectados</li>
                        <li>Toque em "Conectar um aparelho"</li>
                        <li>Escaneie o QR Code abaixo</li>
                      </ol>
                    </div>
                  </div>
                </div>

                <div className="text-center">
                  {qrLoading ? (
                    <div className="space-y-4">
                      <Loader2 className="w-12 h-12 animate-spin text-green-600 mx-auto" />
                      <p className="text-gray-600">Gerando QR Code...</p>
                    </div>
                  ) : qrCode ? (
                    <div className="space-y-4">
                      <div className="inline-block p-4 bg-white border-2 border-gray-200 rounded-2xl shadow-sm">
                        <img 
                          src={qrCode} 
                          alt="QR Code WhatsApp" 
                          className="w-64 h-64 mx-auto"
                        />
                      </div>
                      <div className="flex items-center justify-center gap-2 text-gray-600">
                        <QrCode className="w-4 h-4" />
                        <span className="text-sm">Escaneie com seu WhatsApp</span>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
                        <WifiOff className="w-8 h-8 text-gray-500" />
                      </div>
                      <p className="text-gray-600">
                        Não foi possível gerar o QR Code. 
                        <button 
                          onClick={getQRCode}
                          className="text-green-600 hover:text-green-700 ml-1"
                        >
                          Tentar novamente
                        </button>
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {currentStep === 3 && (
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <MessageSquare className="w-10 h-10 text-green-600" />
            </div>
            
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              WhatsApp QR Configurado! 🎉
            </h2>
            
            <p className="text-gray-600 mb-8">
              Seu funcionário está pronto para atender via WhatsApp
            </p>
            
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-8">
              <div className="flex items-center justify-center gap-2 text-green-700">
                <Phone className="w-5 h-5" />
                <span className="font-medium">Conectado: {connectedNumber}</span>
              </div>
            </div>
            
            <div className="flex items-center justify-center gap-4">
              <button
                onClick={() => navigate('/channels')}
                className="px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-all"
              >
                Voltar aos Canais
              </button>
              
              <button
                onClick={() => navigate(`/channels/${channelId}`)}
                className="px-6 py-3 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 transition-all"
              >
                Ver Detalhes
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConfigureWhatsAppQR;